import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './Badges.css';

export interface XPBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  xp: number;
  size?: 'sm' | 'md' | 'lg';
  /** Override the icon. Defaults to a lightning bolt. */
  icon?: ReactNode;
  /** When true, the number bumps briefly on change (Duolingo pulse). */
  animate?: boolean;
  ref?: Ref<HTMLSpanElement>;
}

/**
 * XPBadge — the gold pill that shows lifetime XP. Sticky on the home top bar.
 * The number can optionally "bump" when XP changes (animate prop).
 */
export function XPBadge({ xp, size = 'md', icon, animate = true, className, ref, ...rest }: XPBadgeProps) {
  const prev = useRef(xp);
  const [bump, setBump] = useState(false);

  useEffect(() => {
    if (!animate) return undefined;
    if (prev.current === xp) return undefined;
    setBump(true);
    const t = window.setTimeout(() => setBump(false), 400);
    prev.current = xp;
    return () => window.clearTimeout(t);
  }, [xp, animate]);

  return (
    <span
      ref={ref}
      className={clsx('fluent-badge', className)}
      data-kind="xp"
      data-size={size}
      aria-label={`${xp} XP`}
      {...rest}
    >
      <span className="fluent-badge__icon" aria-hidden="true">
        {icon ?? <LightningIcon />}
      </span>
      <span className="fluent-badge__value" data-bump={bump || undefined}>
        {xp.toLocaleString('ka-GE')}
      </span>
    </span>
  );
}

function LightningIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M13.5 2L4 14h6.5L9.5 22 20 10h-6.5L13.5 2z" />
    </svg>
  );
}
